import React from 'react';

import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useGetVisitStatistic } from 'src/features/statistic/api/useGetVisitStatistic';
import DurationPicker from 'src/features/statistic/components/DurationPicker';
import WidgetDurationBreakdown from 'src/features/statistic/components/WidgetDurationBreakdown';
import WidgetTotalVisits from 'src/features/statistic/components/WidgetTotalVisits';
import {
    DataKey,
    TransformedBarItem,
    VisitStatisticHourlyBreakdown,
    VisitStatisticHourlyDurationBreakdown,
} from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider.tsx';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

const getDatetimeToday = () => {
    return {
        from_datetime: dayjs().set('hour', 0).set('minute', 0).format(DATE_FORMAT),
        to_datetime: dayjs().format(DATE_FORMAT),
    };
};

export default function VisitStatistics() {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const [dateParams, setDateParams] = React.useState(getDatetimeToday);

    const { parkingId } = useParking();
    const { data, isLoading } = useGetVisitStatistic({ ...dateParams, parkingId });

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.visitStatistics));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const transformPaymentData = React.useCallback(
        ({
            data,
        }: {
            data?: VisitStatisticHourlyBreakdown | VisitStatisticHourlyDurationBreakdown;
        }): {
            dataKeys: DataKey[];
            data: TransformedBarItem[];
        } => {
            if (!data)
                return {
                    dataKeys: [],
                    data: [],
                };

            const dataKeys: DataKey[] = Array.from(
                Object.values(data).reduce((acc, currentValue) => {
                    Object.keys(currentValue).map((key) => acc.add(key));

                    return acc;
                }, new Set<string>()),
            ).map((key) => ({
                title: formatMessage({ id: key.replaceAll('_', '-'), defaultMessage: key }),
                value: key,
            }));

            const newData: TransformedBarItem[] = Object.entries(data).map(([key, item]) => ({
                ...item,
                title: formatMessage({ id: key.replaceAll('_', '-') }),
            }));

            return {
                dataKeys,
                data: newData,
            };
        },
        [formatMessage],
    );

    return (
        <>
            <div className="flex justify-end">
                {/*<Button variant="ghost" className="rounded-lg border">*/}
                {/*    <FontAwesomeIcon icon={ICON_MAP.FILTER} />*/}
                {/*    Filters*/}
                {/*</Button>*/}
                <div className="flex gap-4">
                    {/*<Button variant="ghost" className="rounded-lg border">*/}
                    {/*    <FontAwesomeIcon icon={ICON_MAP.TABLE} />*/}
                    {/*    Show data*/}
                    {/*</Button>*/}
                    <DurationPicker dateParams={dateParams} setDateParams={setDateParams} />
                </div>
            </div>
            <div className="flex flex-1 flex-col gap-6">
                <div className="flex gap-6">
                    <WidgetTotalVisits isLoading={isLoading} data={data?.total_visits_section} />
                    <WidgetDurationBreakdown
                        className="flex-2"
                        title={formatMessage(statisticMessages.durationBreakdown)}
                        isLoading={isLoading}
                        {...transformPaymentData({ data: data?.duration_breakdown_section })}
                    />
                </div>
                <WidgetDurationBreakdown
                    title={formatMessage(statisticMessages.hourlyVisitsBreakdown)}
                    isLoading={isLoading}
                    {...transformPaymentData({ data: data?.hourly_breakdown_section })}
                />
            </div>
        </>
    );
}
